'use client';

import { useCallback, useState } from 'react';

import { useAppEvents } from '@kit/shared/events';

import type { SignupFieldEntry } from '../schemas/signup-custom-fields.schema';

import { useLastAuthMethod } from './use-last-auth-method';
import { useSignUpWithOtpAction } from './use-sign-up-with-otp-action';

type OtpSignUpCredentials = {
  name: string;
  email: string;
  phoneNumber?: string;
  signupFields?: SignupFieldEntry[];
  children?: Array<{ firstName: string; lastName: string }>;
  emailConflictResolution?: 'use-existing' | 'override';
  phoneConflictResolution?: 'use-existing' | 'override';
};

// Surfaced to the container when the server reports phone-matches-guest but the
// typed email differs from the one on file (same shape as the password flow).
export type OtpEmailMismatch = {
  emailHint: string;
  credentials: OtpSignUpCredentials;
};

// Phone counterpart: EMAIL matched a guest whose on-file phone differs from
// the typed one. Same banner-and-replay shape.
export type OtpPhoneMismatch = {
  phoneHint: string;
  credentials: OtpSignUpCredentials;
};

type UseOtpSignUpFlowProps = {
  // Reserved for parity with the password flow's props. The OTP send is
  // triggered server-side inside signUpWithOtpAction (via the Send Email hook),
  // which doesn't take a captcha token, so it's not forwarded here today.
  captchaToken?: string;
  resetCaptchaToken?: () => void;
};

/**
 * Email-OTP sign-up flow — mirrors usePasswordSignUpFlow but verifies via an
 * emailed code rather than a password. On success it exposes signedUpEmail so the
 * container can swap in the code-entry step; for email_mismatch it surfaces the
 * banner; for phone_exists/user_exists it sets a manual error key.
 */
export function useOtpSignUpFlow({
  resetCaptchaToken,
}: UseOtpSignUpFlowProps) {
  const signUpMutation = useSignUpWithOtpAction();
  const appEvents = useAppEvents();
  const { recordAuthMethod } = useLastAuthMethod();
  const [signedUpEmail, setSignedUpEmail] = useState<string | null>(null);
  const [emailMismatch, setEmailMismatch] = useState<OtpEmailMismatch | null>(
    null,
  );
  const [phoneMismatch, setPhoneMismatch] = useState<OtpPhoneMismatch | null>(
    null,
  );
  // Manual error key (resolved by AuthErrorAlert as auth:errors.<message>) for
  // the structured "already exists" outcomes, which RESOLVE the mutation.
  const [manualError, setManualError] = useState<Error | null>(null);

  const signUp = useCallback(
    async (credentials: OtpSignUpCredentials) => {
      if (signUpMutation.isPending) return;

      // Clear ALL outcome state up front — a resubmit with edited values must
      // not stack a new banner next to a stale one (see use-sign-up-flow).
      setManualError(null);
      setEmailMismatch(null);
      setPhoneMismatch(null);

      try {
        const result = await signUpMutation.mutateAsync(credentials);

        if (result.kind === 'email_mismatch') {
          setEmailMismatch({
            emailHint: result.emailHint,
            credentials,
          });
          return;
        }

        if (result.kind === 'phone_mismatch') {
          setPhoneMismatch({
            phoneHint: result.phoneHint,
            credentials,
          });
          return;
        }

        if (result.kind === 'phone_exists') {
          setManualError(new Error('phoneAlreadyRegistered'));
          return;
        }

        if (result.kind === 'email_exists') {
          setManualError(new Error('duplicateEmailError'));
          return;
        }

        if (result.kind === 'user_exists') {
          setManualError(new Error('duplicateEmailError'));
          return;
        }

        setEmailMismatch(null);
        setPhoneMismatch(null);
        setSignedUpEmail(credentials.email);

        recordAuthMethod('otp', { email: credentials.email });

        appEvents.emit({
          type: 'user.signedUp',
          payload: { method: 'otp' },
        });
      } finally {
        resetCaptchaToken?.();
      }
    },
    [signUpMutation, appEvents, recordAuthMethod, resetCaptchaToken],
  );

  const resolveEmailMismatch = useCallback(
    (resolution: 'use-existing' | 'override') => {
      if (!emailMismatch) return;
      const next = {
        ...emailMismatch.credentials,
        emailConflictResolution: resolution,
      };
      setEmailMismatch(null);
      void signUp(next);
    },
    [emailMismatch, signUp],
  );

  const dismissEmailMismatch = useCallback(() => setEmailMismatch(null), []);

  const resolvePhoneMismatch = useCallback(
    (resolution: 'use-existing' | 'override') => {
      if (!phoneMismatch) return;
      const next = {
        ...phoneMismatch.credentials,
        phoneConflictResolution: resolution,
      };
      setPhoneMismatch(null);
      void signUp(next);
    },
    [phoneMismatch, signUp],
  );

  const dismissPhoneMismatch = useCallback(() => setPhoneMismatch(null), []);

  return {
    signUp,
    loading: signUpMutation.isPending,
    error: manualError ?? signUpMutation.error,
    signedUpEmail,
    emailMismatch,
    resolveEmailMismatch,
    dismissEmailMismatch,
    phoneMismatch,
    resolvePhoneMismatch,
    dismissPhoneMismatch,
  };
}
